import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "relative w-full rounded-md overflow-hidden bg-white border flex items-center justify-center",
  style: {"max-height":"200px"}
}
const _hoisted_2 = ["src"]
const _hoisted_3 = {
  key: 1,
  class: "flex flex-col items-center gap-2 py-6"
}
const _hoisted_4 = { class: "text-sm text-muted-foreground" }
const _hoisted_5 = {
  key: 3,
  class: "absolute inset-0 bg-background/70 backdrop-blur-[1px] flex items-center justify-center"
}

import { ref, computed } from 'vue'
import axios from 'axios'
import { useCompanyRegistrationStore } from '@/stores/companyRegistrationStore'

const MAX_SIZE = 2 * 1024 * 1024 // 2MB

export default /*@__PURE__*/_defineComponent({
  __name: 'LogoUploader',
  setup(__props) {

const store = useCompanyRegistrationStore()

const logoPreview = computed(() => store.form.companyLogo)


const fileInputRef = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const isUploading = ref(false)
const isImageLoading = ref(false)


const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp']

function onDragOver() {
  isDragging.value = true
}
function onDragLeave() {
  isDragging.value = false
}
function onDrop(e: DragEvent) {
  isDragging.value = false
  const file = e.dataTransfer?.files?.[0]
  if (file) handleFile(file)
}
function onFileChange(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0]
  if (file) handleFile(file)
}

async function handleFile(file: File) {
  // --- Validate ---
  if (!ALLOWED_TYPES.includes(file.type)) {
    store.logoError = 'Unsupported file type (only PNG, JPG, WEBP allowed).'
  return
  }
  if (file.size > MAX_SIZE) {
    store.logoError = 'File too large (max 2MB).'
    return
  }
  if (!store.draftId) {
    store.logoError = 'Missing draft ID – please complete previous step first.'
    return
  }

  try {
    isUploading.value = true

    // Get presigned URL from store
    const { uploadUrl, publicUrl } = await store.getPresignedLogoUrl(store.draftId, file.type)

    // Upload to S3
    await axios.put(uploadUrl, file, {
      headers: { 'Content-Type': file.type }
    })

    // Update in store
    store.form.companyLogo = `${publicUrl}?t=${Date.now()}`
    store.form.companyLogoType = file.type
  } catch (err) {
    console.error('Upload failed', err)
  } finally {
    isUploading.value = false
  }
}

function handleRemoveLogo() {
  store.form.companyLogo = ''
  store.form.companyLogoType = ''
  if (fileInputRef.value) fileInputRef.value.value = ''
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _cache[6] || (_cache[6] = _createElementVNode("label", { class: "block text-sm font-medium mb-1" }, "Company Logo", -1)),
    _createElementVNode("div", {
      class: _normalizeClass(["relative w-full rounded-lg border-2 border-dashed bg-muted/20 p-4 flex flex-col items-center justify-center text-center transition", isDragging.value ? 'border-brand/70' : 'border-muted']),
      onDragover: _withModifiers(onDragOver, ["prevent"]),
      onDragleave: _withModifiers(onDragLeave, ["prevent"]),
      onDrop: _withModifiers(onDrop, ["prevent"])
    }, [
      (logoPreview.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("img", {
              src: logoPreview.value,
              alt: "Company logo",
              class: "max-h-[180px] max-w-full object-contain"
            }, null, 8, _hoisted_2)
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _cache[3] || (_cache[3] = _createElementVNode("svg", {
              xmlns: "http://www.w3.org/2000/svg",
              class: "w-8 h-8 text-muted-foreground/60",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor"
            }, [
              _createElementVNode("path", {
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                "stroke-width": "1.5",
                d: "M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-9-12v12m0 0L7.5 13.5m4.5 2.25L16.5 13.5"
              })
            ], -1)),
            _createElementVNode("p", _hoisted_4, [
              _cache[1] || (_cache[1] = _createElementVNode("span", { class: "font-medium text-foreground" }, "Drop", -1)),
              _cache[2] || (_cache[2] = _createTextVNode(" your logo here or ")),
              _createElementVNode("button", {
                type: "button",
                class: "underline",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (fileInputRef.value?.click()))
              }, "browse")
            ]),
            _cache[4] || (_cache[4] = _createElementVNode("p", { class: "text-xs text-muted-foreground" }, "PNG, JPG, or WEBP • Max 2MB", -1))
          ])),
      (logoPreview.value && !isUploading.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 2,
            type: "button",
            onClick: handleRemoveLogo,
            class: "absolute top-2 right-2 w-7 h-7 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90",
            "aria-label": "Remove logo"
          }, " ✕ "))
        : _createCommentVNode("", true),
      (isUploading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[5] || (_cache[5] = [
            _createElementVNode("svg", {
              class: "w-6 h-6 animate-spin",
              viewBox: "0 0 24 24",
              fill: "none"
            }, [
              _createElementVNode("circle", {
                class: "opacity-25",
                cx: "12",
                cy: "12",
                r: "10",
                stroke: "currentColor"
              }),
              _createElementVNode("path", {
                class: "opacity-75",
                d: "M4 12a8 8 0 018-8",
                stroke: "currentColor"
              })
            ], -1),
            _createElementVNode("span", { class: "ml-2 text-sm" }, "Uploading…", -1)
          ])))
        : _createCommentVNode("", true),
      _createElementVNode("input", {
        ref_key: "fileInputRef",
        ref: fileInputRef,
        type: "file",
        accept: "image/png,image/jpeg,image/webp",
        class: "hidden",
        onChange: onFileChange
      }, null, 544)
    ], 34),
    _cache[7] || (_cache[7] = _createElementVNode("p", { class: "text-xs text-muted-foreground mt-2" }, " Recommended square logo (e.g., 500×500). Non-square images will be letterboxed (not cropped). ", -1))
  ]))
}
}

})